<template>
  <div class="settings-tab slim-scroll">
    <horizontal-title-tooltip-section
      :title="$t('portfolioManager.poolSidebar.portfolioSettings')"
      size="l"
      spacing="left"
    />
    <horizontal-title-tooltip-section
      :title="$t('portfolioManager.poolSidebar.portfolioView')"
      :tooltip="$t('portfolioManager.poolSidebar.portfolioViewTooltip')"
      size="s"
      spacing="left"
      class="subtitle"
    />
    <div class="switch-container" :class="{ disabled: hasAmounts }">
      {{ $t("portfolioManager.poolSidebar.weights") }}
      <block-switch
        :value="isWholeStocks"
        @input="setIsWholeStocks"
        :disabled="hasAmounts"
        >{{ $t("portfolioManager.poolSidebar.wholeStocks") }}
      </block-switch>
    </div>
    <b-check
      :checked="useFractions"
      @change="setUseFractions"
      v-if="isWholeStocks"
      >{{ $t("portfolioManager.poolSidebar.useFractions") }}</b-check
    >
    <div class="cash-data-root" v-if="!isWholeStocks">
      <horizontal-title-tooltip-section
        :title="$t('portfolioManager.poolSidebar.addCashData')"
        :tooltip="$t('portfolioManager.poolSidebar.addCashDataTooltip')"
        size="s"
        spacing="left"
        class="subtitle"
      />
      <simple-form-input
        type="number"
        :disabled="portfolio === null || editMode"
        :label="$t('portfolioManager.poolSidebar.capital')"
        :value="cash"
        @input="setCash"
        v-debounce:800="handleCashUpdated"
      >
        <template v-slot:trailingIcon>
          <currency-usd :size="18" />
        </template>
      </simple-form-input>
    </div>
    <horizontal-title-tooltip-section
      :title="$t('portfolioManager.poolSidebar.poolSettingsTitle')"
      :tooltip="$t('portfolioManager.hints.poolSettings')"
      class="no-padding"
      size="l"
      spacing="left"
    />
    <div>
      <select-box
        label="name"
        @input="setSelectedPool"
        :value="selectedPool"
        :options="pools"
        name="Pool"
        :loading="!pools.length"
        :clearable="false"
      />
      <b-button
        variant="blank"
        class="ml-auto pt-0 link-button"
        v-b-modal.pools-modal
        :disabled="!pools.length"
      >
        <bookmark-icon /> {{ $t("portfolioManager.poolsModal.title") }}
      </b-button>
      <div class="datepicker-root">
        <horizontal-title-tooltip-section
          :title="$t('portfolioManager.historicalPriceData')"
          :tooltip="$t('portfolioManager.hints.historyDate')"
          size="s"
          spacing="left"
          class="subtitle"
        />
        <calendar-box
          v-model="priceDate"
          :label="$t('portfolioManager.historyDate')"
          :minDate="calendarMinDate"
          :maxDate="calendarMaxDate"
          class="calendar-box"
        />
        <select-box
          label="name"
          :options="resamplingFrequencyOptions"
          @input="setResamplingFrequency"
          :value="resamplingFrequency"
          :name="$t('portfolioManager.poolSidebar.resamplingFrequency')"
          :searchable="false"
          :clearable="false"
        />
      </div>
    </div>
    <limits-form @ruleAdded="addRule" />
    <div v-if="rules.length">
      <div class="separator" />
      <p class="emphasis">
        {{ $t("portfolioManager.poolSidebar.globalLimits") }}
      </p>
      <limit v-for="(limit, i) in rules" v-bind="limit" :key="i" />
    </div>
    <div v-else class="no-limit">
      <cog-icon fillColor="#787878" />
      <span class="nl-text">
        {{ $t("portfolioManager.poolSidebar.noLimitsApplied") }}
      </span>
    </div>
    <pools-modal />
    <create-pool-modal />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import BlockSwitch from "./inputs/BlockSwitch.vue";
import BookmarkIcon from "vue-material-design-icons/BookmarkMultipleOutline.vue";
import CalendarBox from "./inputs/CalendarBox.vue";
import CogIcon from "vue-material-design-icons/CogOutline.vue";
import CreatePoolModal from "./modals/CreatePoolModal.vue";
import CurrencyUsd from "vue-material-design-icons/CurrencyUsd.vue";
import HorizontalTitleTooltipSection from "./inputs/HorizontalTitleTooltipSection.vue";
import Limit from "./Limit.vue";
import LimitsForm from "./forms/LimitsForm.vue";
import MainAlert from "./alerts/MainAlert.vue";
import PoolsModal from "./modals/CustomPoolModal.vue";
import { RESAMPLING_FREQUENCY_OPTIONS } from "../utils/constants";
import SelectBox from "./inputs/SelectBox.vue";
import SimpleFormInput from "./inputs/SimpleFormInput.vue";
import moment from "moment";
export default {
  name: "settings-sidebar-tab",
  components: {
    CalendarBox,
    HorizontalTitleTooltipSection,
    BookmarkIcon,
    LimitsForm,
    Limit,
    CogIcon,
    SelectBox,
    BlockSwitch,
    SimpleFormInput,
    CurrencyUsd,
    PoolsModal,
    CreatePoolModal,
  },
  data() {
    return {
      calendarMinDate: moment("1.1.2015", "DD.MM.YYYY").toDate(),
      calendarMaxDate: moment().subtract(6, "month").toDate(),
      resamplingFrequencyOptions: RESAMPLING_FREQUENCY_OPTIONS(),
    };
  },
  methods: {
    ...mapActions("portfolioManagerStore", ["getPools", "portfolioConversion", "getTickersWithIncompleteData", "getDefaultEfficientFrontier"]),
    ...mapMutations("portfolioManagerStore", [
      "addRule",
      "setDataSince",
      "setIsWholeStocks",
      "setCash",
      "setUseFractions",
      "setResamplingFrequency",
      "setSelectedPool",
    ]),
    handleCashUpdated() {
      this.portfolioConversion({ isConversingPointDetail: !!this.pointDetail });
      this.portfolioConversion({});
      if (this.pointDetail) {
        this.portfolioConversion({
          isConversingPointDetail: !!this.pointDetail,
        });
      }
    },
  },
  computed: {
    ...mapGetters("portfolioManagerStore", [
      "rules",
      "dataSince",
      "duplicatesPrevented",
      "wrongMinMaxPrevented",
      "wrongWeightSumPrevented",
      "portfolio",
      "isOptimizingPortfolio",
      "removedTickers",
      "isWholeStocks",
      "cash",
      "useFractions",
      "resamplingFrequency",
      "pools",
      "selectedPool",
      "editMode",
      "pointDetail",
      "tickersWithMissingData"
    ]),
    hasAmounts() {
      return !(
        this.portfolio &&
        this.portfolio.portfolio_input.custom_portfolio[0].amount &&
        this.portfolio.portfolio_input.custom_portfolio[0].weight
      );
    },
    priceDate: {
      get() {
        return this.dataSince;
      },
      set(newVal) {
        this.setDataSince(newVal);
        this.getTickersWithIncompleteData(newVal);
      },
    },
    tickersWithError() {
      return this.tickersWithMissingData.map((t) => t.ticker);
    },
    errorDate() {
      return this.tickersWithMissingData.reduce((minDate, t) => {
        return minDate && moment(minDate).isAfter(moment(t.start)) ? minDate : t.start;
      }, undefined);
    }
  },
  watch: {
    duplicatesPrevented() {
      this.$toast.info({
        component: MainAlert,
        props: {
          message: this.$i18n.t("validations.sameLimit"),
        },
      });
    },
    wrongMinMaxPrevented() {
      this.$toast.info({
        component: MainAlert,
        props: {
          message: this.$i18n.t("validations.wrongMinMax"),
        },
      });
    },
    wrongWeightSumPrevented() {
      this.$toast.info({
        component: MainAlert,
        props: {
          message: this.$i18n.t("validations.wrongWeightSum"),
        },
      });
    },
    selectedPool() {
      this.getTickersWithIncompleteData(this.priceDate);
      this.getDefaultEfficientFrontier(this.selectedPool);
    },
    tickersWithMissingData: {
      handler(newVal) {
        if (newVal.length > 0) {
          this.$toast.info({
            component: MainAlert,
            props: {
              message: this.$i18n.t(
                this.tickersWithMissingData.length > 1 ? 
                  "validations.missingDataForTickers" : 
                  "validations.missingDataForTicker", {
                    tickers: this.tickersWithError.join(", "),
                    date: moment(this.errorDate).format(moment.localeData().longDateFormat("L"))
              })
            }
          });
          this.priceDate = this.errorDate;
        }
      },
      deep: true  
    },
    portfolio() {
      const setDefaultPool = () => {
        this.setSelectedPool(this.pools.find((pool) => pool.pool_id === -1));
      };

      if (
        this.portfolio &&
        this.portfolio.portfolio_input.global_limits?.pool_id
      ) {
        const portfolioPool = this.portfolio.portfolio_input.global_limits?.pool_id;
        const matchingPool = this.pools.find(
          (pool) => portfolioPool === pool.pool_id
        );
        if (matchingPool) {
          this.setSelectedPool(matchingPool);
        } else {
          setDefaultPool();
        }
      } else {
        setDefaultPool();
      }
    }
  },
  mounted() {
    this.getPools();
  },
};
</script>

<style lang="scss" scoped>
.settings-tab {
  overflow-y: auto;
  max-height: calc(100vh - 130px);
  padding-bottom: 5rem;
  .rerun-btn {
    width: 100%;
    justify-content: center;
    margin-bottom: toRem(10);
  }
  .rerun-container {
    margin-bottom: toRem(20);
    .no-limit {
      text-align: left;
    }
  }
  .separator {
    width: 100%;
    height: 1px;
    background-color: #d5d1fe;
    margin: toRem(25) 0;
  }
  .datepicker-root,
  .cash-data-root {
    .title-container {
      &.subtitle {
        margin-bottom: 0.5rem;
      }
    }
  }
  .link-button {
    padding: 0;
    padding-bottom: 0.7rem;
    margin-top: -3px;
  }
  .title-container {
    margin-bottom: 0.5rem;
    padding-top: toRem(5);
    &.no-padding {
      padding-top: 0;
    }
    &.subtitle {
      padding-top: 0;
      margin-bottom: 0.3rem;
    }
  }
  .calendar-subtext {
    display: block;
    margin-bottom: 1rem;
    color: $inactive;
    font-style: italic;
    text-align: center;
    font-size: toRem(12);
  }
  .no-limit {
    margin-top: toRem(25);
    color: $inactive;
    font-style: italic;
    text-align: center;
    font-size: toRem(12);
    .nl-text {
      margin-left: 5px;
    }
  }
  .custom-changes {
    margin-top: toRem(25);
  }
  .emphasis {
    font-size: $s-font;
    font-weight: 500;
    color: $headingsColor;
  }
  .switch-container {
    margin-bottom: 0.3rem;
  }
  .switch-container.disabled {
    cursor: context-menu;
    opacity: 0.6;
    color: #6c757d;
  }
}
</style>